import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

export default function PrivacyPolicyDialog({
  open,
  onClose,
  onAgree,
  showAgreeButton = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#0f172a',
          color: '#fff',
          py: 2.5,
        }}
      >
        <Typography variant="h5" fontWeight={900}>
          Zypio Privacy Policy & Service Policies
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}
        >
          Customer, merchant, delivery, return, and liability policies.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography variant="h6" fontWeight={900} gutterBottom>
              1. Customer Policies
            </Typography>

            <PolicySection
              title="Shipping & Delivery Policy"
              items={[
                'Delivery timelines apply according to the delivery area, including Colombo 1–15 and other supported regions.',
                'Zypio will make up to 2 delivery attempts before returning the item to the sender.',
                'Claims for damaged goods must be submitted within 6 hours of delivery.',
              ]}
            />

            <PolicySection
              title="COD (Cash on Delivery) Seller / Merchant Policy"
              items={[
                'Zypio supports Cash on Delivery collections for eligible orders.',
                'Collected COD payments will be remitted back to the seller or merchant within 7 days, usually 5 working days.',
                'For courier safety and parcel protection, payment should be collected before the package is opened unless otherwise approved.',
              ]}
            />

            <PolicySection
              title="Returns & Reverse Logistics Policy"
              items={[
                'Failed deliveries will be returned to the original vendor within 7 to 14 days.',
                'For return to shipment (RTS), a return fee will be charged when the shipment is returned to the sender or merchant.',
              ]}
            />

            <PolicySection
              title="Prohibited & Restricted Items Policy"
              items={[
                'Zypio will not carry prohibited or restricted items under any circumstances.',
                'Common restricted items include illegal substances, hazardous materials, flammable liquids, unregistered currency, weapons, and improperly packaged perishable goods.',
              ]}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: '#fff7ed',
              border: '1px solid #fed7aa',
            }}
          >
            <Typography variant="h6" fontWeight={900} gutterBottom>
              2. Risk & Liability Policies
            </Typography>

            <PolicySection
              title="Lost, Damaged, or Delayed Shipments Policy"
              items={[
                'Zypio Delivery shall be liable for loss or damage to packages only if caused by gross negligence.',
                'Liability is capped at LKR 10,000.00.',
                'High-value items must be declared in advance.',
                'Fragile items must follow proper packaging guidelines to be eligible for compensation.',
              ]}
            />

            <PolicySection
              title="Insurance & Declared Value Policy"
              items={[
                'Additional coverage may be provided for high-value and fragile shipments.',
                'Fragile items should be properly packed using protective materials such as bubble wrap.',
                'Declared value and insurance eligibility may depend on the item type, packaging quality, and prior approval.',
              ]}
            />
          </Paper>

          <Alert severity="info" sx={{ borderRadius: 3 }}>
            These policies are provided for customer, seller, and delivery partner awareness.
            Zypio may update these policies from time to time based on operational, legal, and
            service requirements.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
        {showAgreeButton ? (
          <Button
            onClick={onAgree}
            variant="contained"
            sx={{
              borderRadius: 999,
              px: 3,
              fontWeight: 900,
              textTransform: 'none',
            }}
          >
            Agree & Close
          </Button>
        ) : null}

        <Button
          onClick={onClose}
          variant={showAgreeButton ? 'outlined' : 'contained'}
          sx={{
            borderRadius: 999,
            px: 3,
            fontWeight: 900,
            textTransform: 'none',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PolicySection({ title, items }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 0.5 }}>
        {title}
      </Typography>

      <List dense sx={{ py: 0 }}>
        {items.map((item) => (
          <ListItem key={item} sx={{ py: 0.3, alignItems: 'flex-start' }}>
            <ListItemText
              primary={item}
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
                lineHeight: 1.7,
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}